import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AppliedFilters,
  AppliedFilterState,
  FilterRoute,
  FiltersPayload,
  ListFilterKey,
  ListFiltersMap,
  RouteFilterState,
  SortDirection,
  SortOptionsMap,
  TreeFilterKeys,
  TreeFilterMap,
} from './filter-interface';
import { RootState } from '../root/root-types';
import { collectSelectedTreeValueIds, resetTreeSelection } from './helper';

export const FILTER_FEATURE_KEY = 'filtersData';

export interface FiltersState {
  routeFilters: RouteFilterState;
  appliedFilters: AppliedFilterState;
}

const initialState: FiltersState = {
  routeFilters: {},
  appliedFilters: {},
};

export const filtersSlice = createSlice({
  name: FILTER_FEATURE_KEY,
  initialState,
  reducers: {
    updateFiltersByRoute(
      state,
      action: PayloadAction<{
        route: FilterRoute;
        filter: FiltersPayload;
        loading?: boolean;
      }>
    ) {
      const { route, filter, loading } = action.payload;
      const existing = { ...state.routeFilters[route] };

      if (!existing) {
        state.routeFilters[route] = {
          loading: loading ?? false,
          filters: filter,
        };
        return;
      }

      const updatedFilters: FiltersPayload = {
        sortOptions: {
          ...(existing.filters?.sortOptions ?? {}),
          ...filter.sortOptions,
        },
        listFilter: {
          ...(existing.filters?.listFilter ?? {}),
          ...filter.listFilter,
        },
        treeFilter: {
          ...(existing.filters?.treeFilter ?? {}),
          ...filter.treeFilter,
        },
      };

      state.routeFilters[route] = {
        loading: loading ?? existing?.loading ?? false,
        filters: updatedFilters,
      };
    },
    applyFiltersByRoute(state, action: PayloadAction<{ route: FilterRoute }>) {
      const { route } = action.payload;
      const filters = state.routeFilters[route]?.filters || {};
      const applied: AppliedFilters = {};
      Object.entries(filters).forEach(([key, value]) => {
        const typeKey = key as keyof FiltersPayload;
        if (typeKey === 'sortOptions') {
          const appliedSort = Object.entries(value as SortOptionsMap).find(
            (x) => x[1].selected !== false
          )?.[1];
          if (appliedSort)
            applied.sortOptions = {
              field: appliedSort.field,
              direction: appliedSort.selected || SortDirection.Ascending,
            };
        } else if (typeKey === 'listFilter') {
          const appliedList: Partial<Record<ListFilterKey, string[]>> = {};
          Object.entries(value as Partial<ListFiltersMap>).forEach(
            ([key, item]) => {
              const listKey = key as ListFilterKey;
              const selectedItems = item.values
                .filter((x) => x.selected)
                .map((x) => x.id);
              appliedList[listKey] = selectedItems;
            }
          );
          applied.listFilter = appliedList;
        } else if (typeKey === 'treeFilter') {
          const appliedTree: Partial<Record<TreeFilterKeys, string[]>> = {};
          Object.entries(value as Partial<TreeFilterMap>).forEach(
            ([treeKey, treeData]) => {
              appliedTree[treeKey as TreeFilterKeys] =
                collectSelectedTreeValueIds(treeData.value);
            }
          );
          applied.treeFilter = appliedTree;
        }
      });
      state.appliedFilters = { ...state.appliedFilters, [route]: applied };
    },
    clearAppliedFilters(state, action: PayloadAction<{ route: FilterRoute }>) {
      const { route } = action.payload;
      state.appliedFilters[route] = {};
      const filters = state.routeFilters[route]?.filters || {};
      const updatedFilters: FiltersPayload = {};
      Object.entries(filters).forEach(([key, filter]) => {
        const typeKey = key as keyof FiltersPayload;
        if (typeKey === 'sortOptions') {
          const sortOptions = Object.fromEntries(
            Object.entries(filter as SortOptionsMap).map(([sKey, value]) => [
              sKey,
              { ...value, selected: value.isDefault ?? false },
            ])
          ) as SortOptionsMap;
          updatedFilters[typeKey] = sortOptions;
        }
        if (typeKey === 'listFilter') {
          const listFilter = Object.fromEntries(
            Object.entries(filter as Partial<ListFiltersMap>).map(
              ([lKey, data]) => [
                lKey,
                {
                  ...data,
                  values: data.values.map((x) => ({
                    ...x,
                    selected: x.isDefault ?? false,
                  })),
                },
              ]
            )
          ) as Partial<ListFiltersMap>;
          updatedFilters[typeKey] = listFilter;
        }
        if (typeKey === 'treeFilter') {
          const treeFilter = Object.fromEntries(
            Object.entries(filter as Partial<TreeFilterMap>).map(
              ([treeKey, treeData]) => [
                treeKey,
                {
                  ...treeData,
                  value: resetTreeSelection(treeData.value),
                },
              ]
            )
          ) as Partial<TreeFilterMap>;
          updatedFilters[typeKey] = treeFilter;
        }
      });
      state.routeFilters[route] = { loading: false, filters: updatedFilters };
    },
  },
});

export const {
  updateFiltersByRoute,
  applyFiltersByRoute,
  clearAppliedFilters,
} = filtersSlice.actions;

export const getFiltersSliceState = (rootState: RootState): FiltersState =>
  rootState[FILTER_FEATURE_KEY];

export const getFilterByRoute = (route: FilterRoute) =>
  createSelector(
    getFiltersSliceState,
    (state) =>
      state.routeFilters[route] || {
        loading: false,
        filters: {},
      }
  );

export const getAppliedFilters = (route: FilterRoute) =>
  createSelector(
    getFiltersSliceState,
    (state) => state.appliedFilters[route] ?? {}
  );
